<template>
  <div class="upload-command">
    <div
      v-if="label"
      class="upload-command__label"
    >
      {{ label }}
    </div>
    <button
      class="upload-command__command"
      :class="{
        'upload-command__command--wrap': wraps,
      }"
      aria-label="Copy command to clipboard"
      @click="onClick"
    >
      <template v-if="wraps">{{ command }}</template>

      <template v-else>
        <span class="upload-command__prefix">$&nbsp;{{ prefix }}</span>
        {{ mainCommand }}
      </template>

      <IconDuotoneCopy class="upload-command__icon" />
    </button>
  </div>
</template>

<script lang="ts">
export default {
  name: 'CodeBlock',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import IconDuotoneCopy from '@/assets/icons/IconDuotoneCopy.vue'
import { copy as copyToClipboard } from '@/core/utils/clipboard'

const props = withDefaults(
  defineProps<{
    /**
     * The full command to render. Will be copied to the clipboard on click.
     */
    command: string
    /**
     * Label to render above the command.
     */
    label?: string
    /**
     * If true, the command will wrap onto multiple lines. If false, the
     * command will be truncated with ellipsis if it overflows the container.
     */
    wraps?: boolean
  }>(),
  {
    label: '',
    wraps: false,
  },
)

const emit = defineEmits<{
  (event: 'copy:success'): void
  (event: 'copy:failure'): void
}>()

const words = computed(() => props.command.split(' '))

// Pull out the first word so that we can highlight it
const prefix = computed(() => words.value[0] ?? null)

// Join the remaining words back together
const mainCommand = computed(() => words.value.slice(1).join(' ') || null)

const onClick = async (): Promise<void> => {
  try {
    await copyToClipboard(props.command)
  } catch (e) {
    emit('copy:failure')

    return
  }
  emit('copy:success')
}
</script>

<style lang="scss" scoped>
@import '../assets';

.upload-command {
  @include col;

  &__label {
    margin-bottom: $spacing-2;
    color: var(--color-text-subtle);
    @include typography(md, inter);
  }

  &__command {
    @include typography(md, inter);
    position: relative;
    color: var(--color-text-default);
    background: var(--color-background-gray-subtlest);
    padding: 7px 10px;
    padding-right: 35px;
    cursor: pointer;
    border-radius: $borderRadius8;
    overflow: hidden;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    transition: background 0.3s;

    &:hover {
      background: var(--color-background-gray-subtlest-hovered);
    }

    &:focus {
      background: var(--color-background-gray-subtlest-hovered);
    }

    &--wrap {
      @include typography(md-2, source);
      white-space: normal;
      line-height: 150%;
      white-space: pre-wrap;
      overflow: auto;
      max-height: 400px;
      flex-shrink: 1;
      flex-grow: 0;
    }
  }

  &__prefix {
    color: var(--color-text-selected);
  }

  &__icon {
    position: absolute;
    top: 16px;
    right: 8px;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
  }
}
</style>
